import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where, doc, updateDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { loadGradeSections } from "../../services/sectionsService";
import { getCurrentUser } from "../../auth";

export default function ReassignSections() {
  const user = getCurrentUser();
  const [campus, setCampus] = useState(String(user?.campus || "gapan").toLowerCase());
  const [grade, setGrade] = useState("");
  const [preview, setPreview] = useState([]);
  const [sectionsMap, setSectionsMap] = useState({});
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState("");

  useEffect(() => {
    (async () => {
      const m = await loadGradeSections(campus);
      setSectionsMap(m || {});
    })();
  }, [campus]);

  const grades = useMemo(() => Object.keys(sectionsMap || {}), [sectionsMap]);
  const targetSections = useMemo(() => (sectionsMap?.[grade]) || [], [sectionsMap, grade]);

  async function loadStudents() {
    if (!campus || !grade) { setPreview([]); return; }
    const q = query(collection(db, "students"), where("campus", "==", campus), where("gradeLevel", "==", grade));
    const snap = await getDocs(q);
    const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a,b)=>String(a.lastName||"").localeCompare(String(b.lastName||"")) || String(a.firstName||"").localeCompare(String(b.firstName||"")));
    if (targetSections.length === 0) { setPreview(rows.map(s => ({ id: s.id, name: `${s.lastName}, ${s.firstName}`, from: s.section || "", to: "" }))); return; }
    const alloc = [];
    let i = 0;
    for (const s of rows) {
      const to = targetSections[i % targetSections.length];
      alloc.push({ id: s.id, name: `${s.lastName}, ${s.firstName}`, from: s.section || "", to });
      i++;
    }
    setPreview(alloc);
  }

  async function apply() {
    if (preview.length === 0) return;
    setBusy(true); setInfo("Reassigning…");
    try {
      for (const p of preview) {
        if (!p.to || p.to === p.from) continue;
        const ref = doc(db, "students", p.id);
        await updateDoc(ref, { section: p.to, sections: [p.to], updatedAt: new Date().toISOString() });
      }
      setInfo("Reassignment completed.");
    } catch (e) {
      setInfo("Failed to reassign some students.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container mt-4">
      <h3 className="mb-3">Reassign Sections</h3>
      <div className="card p-3 mb-3">
        <div className="row g-2">
          <div className="col-md-3">
            <label className="form-label">Campus</label>
            <select className="form-select" value={campus} onChange={e => setCampus(e.target.value)}>
              <option value="gapan">Gapan</option>
              <option value="bulacan">Bulacan</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Grade</label>
            <select className="form-select" value={grade} onChange={e => setGrade(e.target.value)}>
              <option value="">Select</option>
              {grades.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div className="col-md-3 d-flex align-items-end">
            <button className="btn btn-outline-secondary" onClick={loadStudents}>Preview</button>
          </div>
          <div className="col-md-3 d-flex align-items-end">
            <button className="btn btn-primary" onClick={apply} disabled={busy || preview.length===0}>Apply</button>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light"><tr><th>ID</th><th>Name</th><th>From</th><th>To</th></tr></thead>
            <tbody>
              {preview.map(p => (<tr key={p.id}><td>{p.id}</td><td>{p.name}</td><td>{p.from}</td><td>{p.to}</td></tr>))}
              {preview.length===0 && <tr><td colSpan="4" className="text-center text-muted">No preview</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {info && <div className="alert alert-info mt-3">{info}</div>}
    </div>
  );
}

